import { useMemo } from "react";

import { formatMoney } from "infra/helpers/number";
import moment from "moment";
import Tag from "components/Tags/CustomTag";
import { Tag as TagChakra } from "@chakra-ui/react";
import { Flex, Text } from "@chakra-ui/react";
import { OperacaoEnum, StatusBoletoEnum } from "resources/enums/conta";
import { MdFileDownload, MdOutlineHomeWork } from "react-icons/md";
import type { ColumnsType } from "antd/es/table";
import { ContaRecorrenteLancamentoItemResponse } from "features/conta-recorrente/typings";

function useContaRecorrenteLancamentosTableColumns(operacao?: OperacaoEnum) {
  return useMemo((): ColumnsType<ContaRecorrenteLancamentoItemResponse> => {
    return [
      {
        title: "Vencimento",
        width: "125px",
        render: (item: ContaRecorrenteLancamentoItemResponse) =>
          item.dataVencimento
            ? moment(item.dataVencimento).format("DD/MM/YYYY")
            : "--",
      },
      {
        title: "Descrição",
        dataIndex: "descricao",
      },
      {
        title: operacao === OperacaoEnum.Despesa ? "Fornecedor" : "Devedor",
        render: ({
          pessoa,
          unidade,
        }: ContaRecorrenteLancamentoItemResponse) => (
          <Flex flexDir="column">
            {unidade ? (
              <Flex gap="8px" alignItems={"center"}>
                <MdOutlineHomeWork /> <Text fontWeight="bold">{unidade}</Text>
              </Flex>
            ) : null}
            <Text>{pessoa}</Text>
          </Flex>
        ),
      },
      {
        title: "Valor",
        render: ({
          statusBoletoId,
          ...original
        }: ContaRecorrenteLancamentoItemResponse) => (
          <Flex justifyContent="flex-end" alignItems="center">
            <Flex flex={1} flexDir="column">
              <Flex fontWeight="bold">
                {formatMoney(
                  original.valorLiquido ||
                    original.valorPago ||
                    original.valor ||
                    "--"
                )}{" "}
              </Flex>
              <Flex>
                {statusBoletoId === StatusBoletoEnum.AguardandoRegistro ? (
                  <TagChakra w="130px" colorScheme="gray" size="sm">
                    Boleto em registro
                  </TagChakra>
                ) : null}

                {statusBoletoId === StatusBoletoEnum.Cancelado ? (
                  <TagChakra w="130px" colorScheme="red" size="sm">
                    Boleto Cancelado
                  </TagChakra>
                ) : null}

                {statusBoletoId === StatusBoletoEnum.Erro ? (
                  <TagChakra w="130px" colorScheme="red" size="sm">
                    Erro ao criar boleto
                  </TagChakra>
                ) : null}

                {statusBoletoId === StatusBoletoEnum.EmCancelamento ? (
                  <TagChakra w="170px" colorScheme="yellow" size="sm">
                    Boleto em cancelamento
                  </TagChakra>
                ) : null}

                {statusBoletoId === StatusBoletoEnum.Disponivel ? (
                  <TagChakra w="155px" colorScheme="green" size="sm">
                    <MdFileDownload style={{ marginRight: 4 }} /> Boleto já
                    disponível
                  </TagChakra>
                ) : null}
              </Flex>
            </Flex>
            <Flex flexDir="column" gap="0px">
              <Flex gap="8px" justifyContent="flex-end">
                {original.dataPagamento ? (
                  <Tag
                    h="25px"
                    bg="green.600"
                    minW="85px"
                    color="white"
                    fontSize="10px"
                    textAlign="center"
                  >
                    Pago {moment(original.dataPagamento).format("DD/MM")}
                  </Tag>
                ) : moment(
                    moment(original.dataVencimento).format("YYYY-MM-DD")
                  ).isBefore(moment().format("YYYY-MM-DD")) ? (
                  <Tag
                    h="25px"
                    maxW="170px"
                    bg="red.500"
                    color="white"
                    fontSize="10px"
                    textAlign="center"
                  >
                    Em Atraso{" "}
                    {moment().diff(moment(original.dataVencimento), "days")}{" "}
                    dia(s)
                  </Tag>
                ) : (
                  <Tag
                    h="25px"
                    minW="100px"
                    bg="orange"
                    color="white"
                    fontSize="10px"
                    textAlign="center"
                  >
                    Pendente
                  </Tag>
                )}
              </Flex>

              {original.tag ? (
                <Tag
                  mt="4px"
                  h="25px"
                  bg="gray.100"
                  minW="85px"
                  color="gray.600"
                  fontSize="10px"
                  textAlign="center"
                >
                  {original.tag}
                </Tag>
              ) : null}
            </Flex>
          </Flex>
        ),
      },
    ];
  }, [operacao]);
}

export default useContaRecorrenteLancamentosTableColumns;
